import { useState } from 'react'
import { Navigate } from 'react-router-dom'

import { api, useAuth } from '@forbiz/web-kit'
import type { ReferralInviteResponse, ReferralSummaryResponse } from '@forbiz/contracts'

import { GiftIcon } from '../components/GiftIcon.js'
import { useAsync } from '../lib/useAsync.js'
import styles from '../app.module.css'
import s from './ReferralPage.module.css'

/**
 * 추천하고 포인트 받기 (BIZ-75/132, 회의 S5). '내 회사' 설정(관리자 전용) 안에 묻혀 있던 추천을
 * 독립 페이지로 뺐다 — 홈 배너·프로필 메뉴에서 들어오고, 담당자(MEMBER)도 쓸 수 있다
 * (추천 코드는 사용자 단위라 API가 로그인만 요구한다).
 * 링크로 가입한 회사가 주문하고 배송이 완료되면 주문금액의 일정 비율이 추천 포인트로 쌓인다.
 */

const STEPS: { title: string; body: string }[] = [
  { title: '링크 보내기', body: '아래 버튼으로 내 추천 링크를 카톡·문자로 보내요.' },
  { title: '지인이 가입', body: '그 링크로 가입하면 추천인으로 연결돼요.' },
  { title: '포인트 적립', body: '가입한 회사가 주문하고 배송이 완료되면 포인트가 쌓여요.' },
]

export function ReferralPage() {
  const { user } = useAuth()
  const isCompany = user?.role === 'COMPANY_ADMIN' || user?.role === 'COMPANY_MEMBER'
  const { data, loading, error } = useAsync(() => api.get<ReferralSummaryResponse>('/api/referral'), [])
  const [copied, setCopied] = useState(false)
  const [copyFailed, setCopyFailed] = useState(false)
  const [invitePhone, setInvitePhone] = useState('')
  const [inviting, setInviting] = useState(false)
  const [inviteMsg, setInviteMsg] = useState<{ tone: 'ok' | 'warn'; text: string } | null>(null)

  if (user && !isCompany) return <Navigate to="/orders" replace />

  const phoneValid = /^01[016789][0-9]{7,8}$/.test(invitePhone.replace(/[^0-9]/g, ''))
  const shareLink = data ? `${window.location.origin}/signup?ref=${data.code}` : ''

  // 번호로 초대 발송(BIZ-132). 현재 문자로 나가고, 알림톡 승인 시 자동 승격.
  async function sendInvite() {
    const phone = invitePhone.replace(/[^0-9]/g, '')
    if (!phoneValid || inviting) return
    setInviting(true)
    setInviteMsg(null)
    try {
      const res = await api.post<ReferralInviteResponse>('/api/referral/invite', { phone })
      if (res.ok) {
        setInviteMsg({ tone: 'ok', text: '초대를 보냈어요 (문자로 발송됩니다).' })
        setInvitePhone('')
      } else {
        const map: Record<string, string> = {
          DUPLICATE: '최근에 이미 초대한 번호예요.',
          RATE_LIMITED: '오늘 초대 한도를 넘었어요. 내일 다시 시도해 주세요.',
          DISABLED: '초대 발송이 잠시 중지돼 있어요.',
        }
        setInviteMsg({ tone: 'warn', text: map[res.reason ?? ''] ?? '초대를 보내지 못했어요.' })
      }
    } catch {
      setInviteMsg({ tone: 'warn', text: '초대를 보내지 못했어요. 잠시 후 다시 시도해 주세요.' })
    } finally {
      setInviting(false)
    }
  }

  async function copyLink() {
    if (!shareLink) return
    // 링크를 화면에 따로 보여 주지 않으므로, 클립보드 API가 막힌 환경(일부 인앱 브라우저)은 옛 방식으로 복사한다.
    let ok = false
    try {
      await navigator.clipboard.writeText(shareLink)
      ok = true
    } catch {
      const prev = document.activeElement as HTMLElement | null
      const ta = document.createElement('textarea')
      ta.value = shareLink
      ta.setAttribute('readonly', '')
      ta.style.position = 'fixed'
      ta.style.opacity = '0'
      document.body.appendChild(ta)
      ta.select()
      ta.setSelectionRange(0, ta.value.length) // iOS Safari는 select()만으론 선택이 안 된다
      try {
        ok = document.execCommand('copy')
      } catch {
        ok = false
      }
      ta.remove()
      prev?.focus()
    }
    if (ok) {
      setCopyFailed(false)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } else {
      // 복사가 막힌 환경에서만 링크를 보여 줘 직접 복사할 수 있게 한다.
      setCopyFailed(true)
    }
  }

  // 공유 시트 — 모바일이면 카톡·문자 등 기기 공유. 미지원(데스크톱)은 복사 폴백.
  async function doShare() {
    if (!shareLink) return
    if (navigator.share) {
      try {
        await navigator.share({
          title: '포비즈 초대',
          text: '부고 링크 한 번이면 30초, 화환 주문 끝 — 포비즈에 초대해요.',
          url: shareLink,
        })
      } catch {
        /* 사용자가 공유 시트를 닫음 — 무시 */
      }
    } else {
      await copyLink()
    }
  }

  return (
    <div className={s.page}>
      <div className={s.hero}>
        <span className={s.heroIcon} aria-hidden="true">
          <GiftIcon />
        </span>
        <h1 className={s.title}>지인에게 추천하고 포인트 받기</h1>
        <p className={s.sub}>화환 주문이 잦은 지인·거래처에 포비즈를 알려 주세요.</p>
      </div>

      <ol className={s.steps}>
        {STEPS.map((st, i) => (
          <li key={st.title} className={s.step} style={{ animationDelay: `${i * 60}ms` }}>
            <span className={s.stepNum}>{i + 1}</span>
            <span className={s.stepBody}>
              <span className={s.stepTitle}>{st.title}</span>
              <span className={s.stepText}>{st.body}</span>
            </span>
          </li>
        ))}
      </ol>

      {error ? (
        <div className={styles.error}>{error}</div>
      ) : loading || !data ? (
        <div className={s.card}>
          <div className={s.skel} />
          <div className={s.skel} style={{ width: '60%' }} />
        </div>
      ) : (
        <>
          <section className={s.card} aria-labelledby="ref-share">
            <h2 className={s.cardTitle} id="ref-share">
              초대하기
            </h2>
            <div className={s.shareBtns}>
              <button type="button" data-variant="primary" onClick={() => void doShare()}>
                링크 공유하기
              </button>
              <button type="button" data-variant="secondary" onClick={() => void copyLink()}>
                {copied ? '복사됨 ✓' : '링크 복사'}
              </button>
            </div>
            {copyFailed && (
              <p className={s.copyFallback} role="status">
                이 화면에선 자동 복사가 안 돼요. 아래 링크를 길게 눌러 복사해 주세요.
                <span className={s.copyLink}>{shareLink}</span>
              </p>
            )}

            <div className={s.divider} />
            <label className={s.inviteLabel} htmlFor="ref-phone">
              번호로 초대장 보내기 <span className={s.inviteHint}>문자로 발송돼요</span>
            </label>
            <div className={s.shareRow}>
              <input
                id="ref-phone"
                inputMode="tel"
                placeholder="010-0000-0000"
                value={invitePhone}
                onChange={(e) => setInvitePhone(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void sendInvite()
                }}
                disabled={inviting}
              />
              <button
                type="button"
                data-variant="primary"
                disabled={!phoneValid || inviting}
                onClick={() => void sendInvite()}
              >
                {inviting ? '보내는 중…' : '초대 보내기'}
              </button>
            </div>
            {inviteMsg ? (
              <p role="status" className={inviteMsg.tone === 'ok' ? s.msgOk : s.msgWarn}>
                {inviteMsg.text}
              </p>
            ) : null}
          </section>

          <section className={s.card} aria-labelledby="ref-mine">
            <h2 className={s.cardTitle} id="ref-mine">
              내 추천 현황
            </h2>
            <div className={s.statRow}>
              <div className={s.stat}>
                <div className={s.statLabel}>추천한 사람</div>
                <div className={s.statValue}>{data.referredCount.toLocaleString('ko-KR')}명</div>
              </div>
              <div className={s.stat}>
                <div className={s.statLabel}>받은 포인트</div>
                <div className={s.statValue}>{data.rewardPoints.toLocaleString('ko-KR')}P</div>
              </div>
            </div>
            <div className={s.codeRow}>
              <span className={s.codeLabel}>추천 코드</span>
              <span className={s.codeValue}>{data.code}</span>
            </div>
          </section>
        </>
      )}
    </div>
  )
}
